import React, { Fragment, lazy, Suspense } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

// configs
import { PATH_NAME, USER_ROLE } from 'configs';

// types
import { IRoutes } from 'models/IRoutes';

// layouts
import MainLayout from 'layouts/MainLayout';

// containers
import AuthGuard from 'guards/AuthGuard';
import GuestGuard from 'guards/GuestGuard';

// route

import FeedbackList from 'features/Feedback/FeedbackList';
import FeedbackEdit from 'features/Feedback/FeedbackEdit';
import GuidelineCircularsView from 'features/Guideline&Circulars/Guideline&CircularsView/Guideline&CircularsView';
import GuidelineCircularsAdd from 'features/Guideline&Circulars/Guideline&CircularsAdd';
import GuidelineCircularsEdit from 'features/Guideline&Circulars/Guideline&CircularsEdit';
import LatestUpdateList from 'features/LatestUpdate/LatestUpdateView/LatestUpdateList';
import LatestUpdateAdd from 'features/LatestUpdate/LatestUpdateAdd/LatestUpdateAdd';
import LatestUpdateEdit from 'features/LatestUpdate/LatestUpdateEdit';
import RoleRoute from './RoleRoute';
import UpdatePassword from 'features/Login/UpdatePassword';

// modules
const CategoryList = lazy(() => import('features/Category/CategoryList'));
const Error404View = lazy(() => import('features/Error404View'));
const DenyView = lazy(() => import('features/DenyView'));
const ProductAdd = lazy(() => import('features/Product/ProductAdd'));
const ProductList = lazy(() => import('features/Product/ProductList'));
const DepartmentMasterList = lazy(() => import('features/DepartmentMaster/DepartmentMasterList/DepartmentMasterList'));
const DesignationMasterList = lazy(() => import('features/DesignationMaster/DesignationMasterList/DesignationMasterList'));
const UserAdd = lazy(() => import('features/Master/UserMaster/UserAdd/UserAdd'));
const UserList = lazy(() => import('features/Master/UserMaster/UserList/UserList'));
const UserPendingStateList = lazy(() => import('features/Master/UserPendingState/UserPendingStateList/UserStatePendingList'));
const UserPendingAdminList = lazy(() => import('features/Master/UserPendingAdmin/UserPendingAdminList/UserAdminPendingList'));
const AssignmentMasterList = lazy(() => import('features/ComplaintAssignment/AssignmentMaster/AssignmentMasterList'));
const AssignmentMasterAdd = lazy(
  () => import('features/ComplaintAssignment/AssignmentMaster/AssignmentMasterAdd/AssignmentMasterAdd'),
);
const UserUpdate = lazy(() => import('features/Master/UserMaster/UserUpdate'));

const AssignmentMasterEdit = lazy(
  () => import('features/ComplaintAssignment/AssignmentMaster/AssignmentMastrerEdit/AssignmentMasterEdit'),
);
const ComplaintViewList = lazy(() => import('features/ComplaintView/ComplaintViewList/ComplaintViewList'));
const ComplaintViewEdit = lazy(() => import('features/ComplaintView/ComplaintViewEdit/ComplaintViewEdit'));
const SubCategoryList = lazy(() => import('features/SubCategory/SubCategoryList'));
const EventsList = lazy(() => import('features/Events/EventList/EventList'));
const EventAdd = lazy(() => import('features/Events/EventAdd/EventAdd'));
const EventUpdate = lazy(() => import('features/Events/EventUpdate/EventUpdate'));
const ResourceList = lazy(() => import('features/Resources/ResourceList/ResourceList'));
const ResourceAdd = lazy(() => import('features/Resources/ResourceAdd/ResourceAdd'));
const ResourceUpdate = lazy(() => import('features/Resources/ResourceUpdate/ResourceUpdate'));
const HelplineList = lazy(() => import('features/Helplines/HelplineList/HelplineList'));
const HelplineAdd = lazy(() => import('features/Helplines/HelplineAdd/HelplineAdd'));
const HelplineUpdate = lazy(() => import('features/Helplines/HelplineUpdate/HelplineUpdate'));
const NotificationAdd = lazy(() => import('features/Notifications/NotificationAdd/NotificationAdd'));
const InformationList = lazy(() => import('features/Information/InformationList/InformationList'));
const InformationAdd = lazy(() => import('features/Information/InformationAdd/InformationAdd'));
const InformationEdit = lazy(() => import('features/Information/InformationEdit/InformationEdit'));
const OpportunityList = lazy(() => import('features/Opportunity/OpportunityList/OpportunityList'));
const OpportunityAdd = lazy(() => import('features/Opportunity/OpportunityAdd/OpportunityAdd'));
const OpportunityEdit = lazy(() => import('features/Opportunity/OpportunityEdit/OpportunityEdit'));
const UserOpportunities = lazy(() => import('features/Opportunity/UserOpportunities/UserOpportunities'));
const SchemesList = lazy(() => import('features/Schemes/SchemesList/SchemesList'));
const SchemesAdd = lazy(() => import('features/Schemes/SchemesAdd/SchemesAdd'));
const SchemesEdit = lazy(() => import('features/Schemes/SchemesEdit/SchemesEdit'));
const Users = lazy(() => import('features/Users'));
const Dashboard = lazy(() => import('features/Dashboard'));
const Playbackground = lazy(() => import('features/Playbackground'));
const Login = lazy(() => import('features/Login'));
const Kanban = lazy(() => import('features/Kanban'));
const VerifiedPage = lazy(() => import('features/VerifiedPage/VerifiedPage'));
const AdminComplaintReassignment = lazy(() => import('features/ComplaintReassignment/AdminComplaintReassignment'));
const SiteFeedback = lazy(() => import('features/SiteFeedback/SiteFeedback'));

const routesConfig: IRoutes[] = [
  {
    exact: true,
    path: '/',
    component: () => <Redirect to={PATH_NAME.DASHBOARD} />,
  },
  {
    exact: true,
    path: PATH_NAME.ERROR_404,
    component: Error404View,
  },
  {
    exact: true,
    guard: GuestGuard,
    path: PATH_NAME.LOGIN,
    component: Login,
  },
  {
    exact: true,
    path: PATH_NAME.ERROR_403,
    component: DenyView,
  },
  {
    exact: true,
    path: PATH_NAME.VERIFY,
    component: VerifiedPage,
  },
  {
    exact: true,
    path: PATH_NAME.UPDATE_PASSWORD,
    component: UpdatePassword,
  },
  {
    path: '/',
    guard: AuthGuard,
    layout: MainLayout,
    routes: [
      {
        exact: true,
        path: PATH_NAME.DASHBOARD,
        component: Dashboard,
        requireRoles: [
          USER_ROLE.ADMIN,
          USER_ROLE.STATE_USER,
          USER_ROLE.DISTRICT_USER,
          USER_ROLE.AUTHORITY_USER,
          USER_ROLE.CHIEF_COMMISSIONER,
          USER_ROLE.STATE_COMMISSIONER,
          USER_ROLE.DISTRICT_COLLECTOR,
          USER_ROLE.DePWD_USER,
          USER_ROLE.NODAL_OFFICER,
        ],
      },
      {
        exact: true,
        path: PATH_NAME.PLAY_BACKGROUND,
        component: Playbackground,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.STATE_USER, USER_ROLE.CHIEF_COMMISSIONER, USER_ROLE.STATE_COMMISSIONER],
      },

      // MASTER //
      {
        exact: true,
        path: PATH_NAME.COUNTRY_MASTER_LIST,
        component: ProductList,
        requireRoles: [USER_ROLE.ADMIN],
      },
      {
        exact: true,
        path: PATH_NAME.STATE_MASTER_LIST,
        component: ProductAdd,
        requireRoles: [USER_ROLE.ADMIN],
      },
      {
        exact: true,
        path: PATH_NAME.DISTRICT_MASTER_LIST,
        component: ProductList,
        requireRoles: [USER_ROLE.ADMIN],
      },
      {
        exact: true,
        path: PATH_NAME.SUB_DISTRICT_MASTER_LIST,
        component: ProductList,
        requireRoles: [USER_ROLE.ADMIN],
      },
      {
        exact: true,
        path: PATH_NAME.USER_MASTER_LIST,
        component: UserList,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.CHIEF_COMMISSIONER],
      },
      {
        exact: true,
        path: PATH_NAME.USER_MASTER_ADD,
        component: UserAdd,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.STATE_USER, USER_ROLE.CHIEF_COMMISSIONER, USER_ROLE.STATE_COMMISSIONER],
        // requireRoles: [USER_ROLE.ADMIN, USER_ROLE.CHIEF_COMMISSIONER],
      },
      {
        exact: true,
        path: `${PATH_NAME.USER_MASTER_EDIT}/:id`,
        component: UserUpdate,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.STATE_COMMISSIONER],
      },
      {
        exact: true,
        path: PATH_NAME.USER_PENDING_SC_LIST,
        component: UserPendingStateList,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.STATE_COMMISSIONER],
      },
      {
        exact: true,
        path: PATH_NAME.USER_PENDING_ADMIN_LIST,
        component: UserPendingAdminList,
        requireRoles: [USER_ROLE.ADMIN],
      },
      {
        exact: true,
        path: PATH_NAME.CATEGORY_MASTER_LIST,
        component: CategoryList,
        requireRoles: [USER_ROLE.ADMIN],
      },
      {
        exact: true,
        path: PATH_NAME.SUB_CATEGORY_MASTER_LIST,
        component: SubCategoryList,
        requireRoles: [USER_ROLE.ADMIN],
      },
      {
        exact: true,
        path: PATH_NAME.DEPARTMENT_MASTER_LIST,
        component: DepartmentMasterList,
        requireRoles: [USER_ROLE.ADMIN],
      },
      {
        exact: true,
        path: PATH_NAME.DESIGNATION_MASTER_LIST,
        component: DesignationMasterList,
        requireRoles: [USER_ROLE.ADMIN],
      },

      // Complaint Assignment
      {
        exact: true,
        path: PATH_NAME.ASSIGNMENT_MASTER_LIST,
        component: AssignmentMasterList,
        requireRoles: [USER_ROLE.ADMIN],
      },
      {
        exact: true,
        path: PATH_NAME.ASSIGNMENT_MASTER_ADD,
        component: AssignmentMasterAdd,
        requireRoles: [USER_ROLE.ADMIN],
      },

      {
        exact: true,
        path: `${PATH_NAME.ASSIGNMENT_MASTER_EDIT}/:id`,
        component: AssignmentMasterEdit,
        requireRoles: [USER_ROLE.ADMIN],
      },

      // Complaint View
      {
        exact: true,
        path: PATH_NAME.COMPLAINT_VIEW_LIST,
        component: ComplaintViewList,
        requireRoles: [
          USER_ROLE.ADMIN,
          USER_ROLE.STATE_USER,
          USER_ROLE.DISTRICT_USER,
          USER_ROLE.AUTHORITY_USER,
          USER_ROLE.CHIEF_COMMISSIONER,
          USER_ROLE.DISTRICT_COLLECTOR,
          USER_ROLE.STATE_COMMISSIONER,
          USER_ROLE.DePWD_USER,
          USER_ROLE.NODAL_OFFICER,
        ],
      },
      // {
      //   exact: true,
      //   path: PATH_NAME.POSITIVE_FEEDBACK_LIST,
      //   component: FeedbackList,
      //   requireRoles: [USER_ROLE.ADMIN],
      // },
      // {
      //   exact: true,
      //   path: `${PATH_NAME.POSITIVE_FEEDBACK_View}/:id`,
      //   component: FeedbackEdit,
      //   requireRoles: [USER_ROLE.ADMIN],
      // },
      {
        exact: true,
        path: `${PATH_NAME.COMPLAINT_VIEW_EDIT}/:id`,
        component: ComplaintViewEdit,
        requireRoles: [
          USER_ROLE.ADMIN,
          USER_ROLE.STATE_USER,
          USER_ROLE.DISTRICT_USER,
          USER_ROLE.AUTHORITY_USER,
          USER_ROLE.CHIEF_COMMISSIONER,
          USER_ROLE.DISTRICT_COLLECTOR,
          USER_ROLE.STATE_COMMISSIONER,
          USER_ROLE.DePWD_USER,
          USER_ROLE.NODAL_OFFICER,
        ],
      },

      {
        exact: true,
        path: PATH_NAME.GUIDELINES_AND_CIRCULARS_LIST,
        component: GuidelineCircularsView,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.DePWD_USER],
      },

      //Event
      {
        exact: true,
        path: PATH_NAME.EVENT_LIST,
        component: EventsList,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.DePWD_USER],
      },
      {
        exact: true,
        path: PATH_NAME.EVENT_ADD,
        component: EventAdd,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.DePWD_USER],
      },
      {
        exact: true,
        path: `${PATH_NAME.EVENT_UPDATE}/:id`,
        component: EventUpdate,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.DePWD_USER],
      },

      //Resources
      {
        exact: true,
        path: PATH_NAME.RESOURCES_LIST,
        component: ResourceList,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.DePWD_USER],
      },
      {
        exact: true,
        path: PATH_NAME.RESOURCE_ADD,
        component: ResourceAdd,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.DePWD_USER],
      },
      {
        exact: true,
        path: `${PATH_NAME.RESOURCE_UPDATE}/:id`,
        component: ResourceUpdate,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.DePWD_USER],
      },

      //Helplines
      {
        exact: true,
        path: PATH_NAME.HELPLINES_LIST,
        component: HelplineList,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.DePWD_USER],
      },
      {
        exact: true,
        path: PATH_NAME.HELPLINE_ADD,
        component: HelplineAdd,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.DePWD_USER],
      },
      {
        exact: true,
        path: `${PATH_NAME.HELPLINE_UPDATE}/:id`,
        component: HelplineUpdate,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.DePWD_USER],
      },

      //Notifications
      {
        exact: true,
        path: PATH_NAME.NOTIFICATION_SEND,
        component: NotificationAdd,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.DePWD_USER],
      },
      {
        exact: true,
        path: `${PATH_NAME.INFORMATION_LIST}`,
        component: InformationList,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.DePWD_USER],
      },
      {
        exact: true,
        path: `${PATH_NAME.INFORMATION_ADD}`,
        component: InformationAdd,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.DePWD_USER],
      },
      {
        exact: true,
        path: `${PATH_NAME.INFORMATION_EDIT}/:id`,
        component: InformationEdit,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.DePWD_USER],
      },
      {
        exact: true,
        path: `${PATH_NAME.OPPORTUNITY_LIST}`,
        component: OpportunityList,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.DePWD_USER],
      },
      {
        exact: true,
        path: `${PATH_NAME.OPPORTUNITY_ADD}`,
        component: OpportunityAdd,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.DePWD_USER],
      },
      {
        exact: true,
        path: `${PATH_NAME.OPPORTUNITY_EDIT}/:id`,
        component: OpportunityEdit,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.DePWD_USER],
      },
      {
        exact: true,
        path: `${PATH_NAME.USER_OPPORTUNITIES_LIST}`,
        component: UserOpportunities,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.DePWD_USER],
      },
      {
        exact: true,
        path: `${PATH_NAME.SCHEMES_LIST}`,
        component: SchemesList,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.DePWD_USER],
      },
      {
        exact: true,
        path: `${PATH_NAME.SCHEMES_ADD}`,
        component: SchemesAdd,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.DePWD_USER],
      },
      {
        exact: true,
        path: `${PATH_NAME.SCHEMES_EDIT}/:id`,
        component: SchemesEdit,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.DePWD_USER],
      },

      {
        exact: true,
        path: PATH_NAME.GUIDELINES_AND_CIRCULARS_ADD,
        component: GuidelineCircularsAdd,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.DePWD_USER],
      },

      {
        exact: true,
        path: `${PATH_NAME.GUIDELINES_AND_CIRCULARS_EDIT}/:id`,
        component: GuidelineCircularsEdit,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.DePWD_USER],
      },

      {
        exact: true,
        path: PATH_NAME.LATEST_UPDATE_LIST,
        component: LatestUpdateList,
        requireRoles: [USER_ROLE.ADMIN],
      },

      {
        exact: true,
        path: PATH_NAME.LATEST_UPDATE_ADD,
        component: LatestUpdateAdd,
        requireRoles: [USER_ROLE.ADMIN],
      },

      {
        exact: true,
        path: `${PATH_NAME.LATEST_UPDATE_EDIT}/:id`,
        component: LatestUpdateEdit,
        requireRoles: [USER_ROLE.ADMIN],
      },

      {
        exact: true,
        path: PATH_NAME.KANBAN,
        component: Kanban,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.LEAD],
      },
      {
        exact: true,
        path: PATH_NAME.USERS,
        component: Users,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.LEAD],
      },

      {
        exact: true,
        path: PATH_NAME.KANBAN,
        component: Kanban,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.LEAD],
      },
      {
        exact: true,
        path: PATH_NAME.USERS,
        component: Users,
        requireRoles: [USER_ROLE.ADMIN, USER_ROLE.LEAD],
      },
      {
        exact: true,
        path: PATH_NAME.COMPLAINT_REASSIGNMENT_LIST,
        component: AdminComplaintReassignment,
        requireRoles: [USER_ROLE.ADMIN],
      },
      {
        exact: true,
        path: PATH_NAME.FEEDBACK_FORM,
        component: SiteFeedback,
        requireRoles: [
          USER_ROLE.ADMIN,
          USER_ROLE.STATE_USER,
          USER_ROLE.DISTRICT_USER,
          USER_ROLE.AUTHORITY_USER,
          USER_ROLE.CHIEF_COMMISSIONER,
          USER_ROLE.STATE_COMMISSIONER,
          USER_ROLE.DISTRICT_COLLECTOR,
          USER_ROLE.DePWD_USER,
          USER_ROLE.NODAL_OFFICER
        ]
      },
      {
        component: () => <Redirect to={PATH_NAME.ERROR_404} />,
      },
    ],
  },

  {
    path: '*',
    routes: [
      {
        exact: true,
        path: '/app',
        component: MainLayout,
      },
      {
        component: () => <Redirect to={PATH_NAME.ERROR_404} />,
      },
    ],
  },
];

const renderRoutes = (routes: IRoutes[]) => {
  return (
    <>
      {routes ? (
        // @ts-ignore
        <Suspense fallback={<div />}>
          <Switch>
            {routes.map((route: IRoutes, idx: number) => {
              const Guard = route.guard || Fragment;
              const Layout = route.layout || Fragment;
              const Component = route.component;
              const requireRoles = route.requireRoles || [];

              return (
                <Route
                  key={`routes-${idx}`}
                  path={route.path}
                  exact={route.exact}
                  render={(props: any) => (
                    // @ts-ignore
                    <Guard>
                      {/* @ts-ignore */}
                      <Layout>
                        {route.routes ? (
                          renderRoutes(route.routes)
                        ) : (
                          <RoleRoute requireRoles={requireRoles}>
                            <Component {...props} />
                          </RoleRoute>
                        )}
                      </Layout>
                    </Guard>
                  )}
                />
              );
            })}
          </Switch>
        </Suspense>
      ) : null}
    </>
  );
};

function Routes() {
  return renderRoutes(routesConfig);
}

export default Routes;
